import { useCallback } from '@wordpress/element';

interface FormFieldDetectionOptions {
	formId: number;
	bookingFieldId: number;
	resourceFieldIds: number[];
}

interface FormFieldMapping {
	bookingFieldId: number | null;
	resourceValues: number[];
	timeFieldElement: HTMLInputElement | null;
}

export function useFormFieldDetection({
	formId,
	bookingFieldId,
	resourceFieldIds,
}: FormFieldDetectionOptions) {
	const detectFormFields = useCallback((): FormFieldMapping => {
		const form = document.querySelector(`#gform_${formId}`);

		if (!form) {
			return {
				bookingFieldId: null,
				resourceValues: [],
				timeFieldElement: null,
			};
		}

		let timeFieldElement: HTMLInputElement | null = null;

		// Get resource values for our specific resource fields
		const resourceValues: number[] = [];
		resourceFieldIds.forEach((fieldId) => {
			const fieldElement = form.querySelector(
				`#field_${formId}_${fieldId}`
			);

			if (
				fieldElement &&
				fieldElement.getAttribute('data-conditional-logic') === 'hidden'
			) {
				return; // Skip conditionally hidden fields
			}

			// Try to get resource value from input (works for manual, static, and automatic modes)
			const resourceInput = form.querySelector(
				`#input_${formId}_${fieldId}`
			) as HTMLInputElement | HTMLSelectElement;

			if (resourceInput?.value) {
				const resourceId = parseInt(resourceInput.value, 10);
				if (!isNaN(resourceId)) {
					resourceValues.push(resourceId);
				}
			}

			// TODO figure out what to do for automatic resource allocation
		});

		// Find the specific booking time field for this booking field
		const bookingField = form.querySelector(
			`#field_${formId}_${bookingFieldId}`
		);
		if (bookingField) {
			// Look for data-time-field on the .ginput_container inside the booking field
			const ginputContainer =
				bookingField.querySelector('.ginput_container');
			const timeFieldAttr =
				ginputContainer?.getAttribute('data-time-field');
			if (timeFieldAttr) {
				const timeFieldId = parseInt(timeFieldAttr, 10);
				timeFieldElement = form.querySelector(
					`#input_${formId}_${timeFieldId}`
				) as HTMLInputElement;
			}
		}

		// Fallback: look for booking time field linked to our specific booking field
		if (!timeFieldElement) {
			const timeFields = form.querySelectorAll(
				'.gfield--input-type-gpb_booking_time'
			);
			for (const timeField of Array.from(timeFields)) {
				const linkedBookingField =
					timeField.getAttribute('data-booking-field');
				if (
					linkedBookingField &&
					parseInt(linkedBookingField, 10) === bookingFieldId
				) {
					const timeFieldId = parseInt(
						timeField.id.split('_').pop() || '0',
						10
					);
					timeFieldElement = form.querySelector(
						`#input_${formId}_${timeFieldId}`
					) as HTMLInputElement;
					break;
				}
			}
		}

		return {
			bookingFieldId,
			resourceValues,
			timeFieldElement,
		};
	}, [formId, bookingFieldId, resourceFieldIds]);

	const getBookingFieldElement = useCallback((): HTMLElement | null => {
		const form = document.querySelector(`#gform_${formId}`);
		return form?.querySelector('.gfield--input-type-gpb_booking') || null;
	}, [formId]);

	const getPriceInput = useCallback(
		(fieldId: number): HTMLInputElement | null => {
			return document.querySelector(
				`#ginput_base_price_${formId}_${fieldId}`
			) as HTMLInputElement;
		},
		[formId]
	);

	return {
		detectFormFields,
		getBookingFieldElement,
		getPriceInput,
	};
}
